import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Send, Loader, Database, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import './AiAssistant.css';

const SUGGESTIONS = [
  'How many calls mentioned وزارة التجارة this week?',
  'Which agent handled the most negative-sentiment calls?',
  'Show urgent calls about delivery this month',
  'What is our overall NPS score and who are the top 5 agents?',
  'How many 1-star reviews in the last 2 weeks?',
];

const AiAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);  // {role, content, sources?}
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    const nextMessages = [...messages, { role: 'user', content }];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);
    try {
      const res = await api.post('/api/ai/chat', {
        messages: nextMessages.map(m => ({ role: m.role, content: m.content })),
      });
      const answer = res.data?.answer || '(no answer)';
      const toolSteps = res.data?.toolSteps || [];
      // Pull citations out of tool results for the sources strip.
      // Results can be either a plain array or {results, note} when the tool
      // has a capacity warning. Unwrap either shape.
      const sources = [];
      for (const step of toolSteps) {
        const hits = Array.isArray(step.result)
          ? step.result
          : (Array.isArray(step.result?.results) ? step.result.results : []);
        for (const hit of hits.slice(0, 5)) {
          if (step.name === 'search_calls' && hit.id) {
            sources.push({
              kind: 'call',
              id: hit.id,
              title: `${hit.agent || 'Unknown agent'} — ${hit.caller || '?'}`,
              subtitle: `${(hit.start_time || '').slice(0, 16).replace('T', ' ')} · ${hit.sentiment || '—'}${hit.urgent ? ' · urgent' : ''}`,
              snippet: hit.snippet,
              url: `/calls/${hit.id}`,
            });
          }
          if (step.name === 'search_reviews' && hit.id) {
            sources.push({
              kind: 'review',
              id: hit.id,
              title: `${hit.reviewer || 'Reviewer'} — ${hit.stars ?? '?'}★`,
              subtitle: `${hit.branch || '—'} · ${(hit.published_at || '').slice(0, 10)}`,
              snippet: hit.snippet,
              url: hit.url,
              external: true,
            });
          }
        }
      }
      setMessages(m => [...m, {
        role: 'assistant',
        content: answer,
        sources,
        toolSteps: toolSteps.map(s => ({ name: s.name, args: s.args })),
      }]);
    } catch (err) {
      console.error('AI chat error:', err);
      setMessages(m => [...m, {
        role: 'assistant',
        content: `Something went wrong: ${err?.response?.data?.error || err.message}`,
        error: true,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const clearConversation = () => setMessages([]);

  return (
    <>
      {/* Floating launcher */}
      <motion.button
        className={`ai-fab ${open ? 'open' : ''}`}
        onClick={() => setOpen(o => !o)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={open ? 'Close AI assistant' : 'Open AI assistant'}
        title={open ? 'Close AI assistant' : 'Ask AI'}
      >
        <Sparkles size={20} />
      </motion.button>

      {/* Slide-in panel */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="ai-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              className="ai-panel"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
            >
              <div className="ai-panel-header">
                <div className="ai-panel-title">
                  <Sparkles size={18} />
                  <span>AI Assistant</span>
                </div>
                <div className="ai-panel-actions">
                  {messages.length > 0 && (
                    <button className="ai-link-btn" onClick={clearConversation}>Clear</button>
                  )}
                  <button className="ai-icon-btn" onClick={() => setOpen(false)} aria-label="Close">
                    <X size={18} />
                  </button>
                </div>
              </div>

              <div className="ai-messages" ref={scrollRef}>
                {messages.length === 0 && (
                  <div className="ai-empty">
                    <div className="ai-empty-icon"><Sparkles size={28} /></div>
                    <h4>Ask anything about your data</h4>
                    <p>I can search Avaya call transcripts, Google Maps reviews, and their AI analysis. Try:</p>
                    <div className="ai-suggestions">
                      {SUGGESTIONS.map(s => (
                        <button key={s} className="ai-suggestion" onClick={() => send(s)}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((m, i) => (
                  <div key={i} className={`ai-msg ai-msg-${m.role} ${m.error ? 'error' : ''}`}>
                    <div className="ai-msg-bubble">
                      {m.content.split('\n').map((line, j) => <div key={j}>{line || '\u00A0'}</div>)}
                    </div>
                    {m.toolSteps && m.toolSteps.length > 0 && (
                      <div className="ai-msg-tools">
                        <Database size={12} />
                        {m.toolSteps.map((t, j) => (
                          <span key={j} className="ai-tool-chip">
                            {t.name}
                            {t.args?.query ? `("${t.args.query}")` : ''}
                            {t.args?.groupBy ? `(by ${t.args.groupBy})` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                    {m.sources && m.sources.length > 0 && (
                      <div className="ai-msg-sources">
                        {m.sources.map((s, j) => (
                          s.external
                            ? <a key={j} className="ai-source-card" href={s.url} target="_blank" rel="noreferrer">
                                <SourceInner s={s} external />
                              </a>
                            : <Link key={j} className="ai-source-card" to={s.url} onClick={() => setOpen(false)}>
                                <SourceInner s={s} />
                              </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

              </div>

              <div className="ai-input-wrap">
                <AnimatePresence>
                  {loading && (
                    <motion.div
                      className="ai-thinking-line"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.15 }}
                    >
                      <Loader size={12} className="spinning" />
                      <span>Searching your data…</span>
                    </motion.div>
                  )}
                </AnimatePresence>
                <div className="ai-input-row">
                  <textarea
                    className="ai-input"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Ask in English or Arabic… (Enter to send, Shift+Enter for newline)"
                    rows={2}
                    disabled={loading}
                  />
                  <button
                    className="ai-send"
                    onClick={() => send()}
                    disabled={!input.trim() || loading}
                    aria-label="Send"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

const SourceInner = ({ s, external }) => (
  <>
    <div className="ai-source-head">
      <span className={`ai-source-kind ai-source-kind-${s.kind}`}>{s.kind}</span>
      <span className="ai-source-title">{s.title}</span>
      {external && <ExternalLink size={11} />}
    </div>
    {s.subtitle && <div className="ai-source-sub">{s.subtitle}</div>}
    {s.snippet && <div className="ai-source-snip">{s.snippet}</div>}
  </>
);

export default AiAssistant;
